import React, { useRef, useState } from 'react';
import { Box, FormControl, Select, MenuItem, OutlinedInput, Typography } from '@mui/material';
import { IconChevronDown } from '@tabler/icons-react';

const colors = {
  border: 'var(--Gray---Line-Stroke, #71767A)',
  primary: '#2F54A5',
  heading: 'var(--black-heading-text-icons, #000)',
  subtitle: '#121A22'
};

const controlTokens = {
  border: '#a2a3a3',
  focus: '#068DF1',
  selectHeight: 48,
  radius: '16px'
};

const selectMenuPaperSx = {
  mt: 0,
  borderColor: controlTokens.focus,
  borderStyle: 'solid',
  borderWidth: 2,
  borderTopWidth: 2,
  borderRadius: '0 0 16px 16px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.14)',
  overflow: 'hidden',
  '& .MuiMenuItem-root[aria-selected="true"]': {
    backgroundColor: '#D8E6FF !important',
  },
  '& .MuiMenuItem-root[aria-selected="true"]:hover': {
    backgroundColor: '#D8E6FF !important',
  }
};

const selectMenuListSx = {
  py: 0,
  px: 0
};

const selectMenuProps = {
  PaperProps: { sx: selectMenuPaperSx },
  MenuListProps: { sx: selectMenuListSx },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  marginThreshold: 8
};

const menuItemBaseSx = {
  fontFamily: 'Noto Sans',
  fontSize: 14,
  fontWeight: 400,
  color: colors.subtitle,
  minHeight: 40,
  height: 40,
  px: 2,
  '&.Mui-selected': {
    backgroundColor: '#D8E6FF',
    fontWeight: 600,
    color: colors.heading
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#D8E6FF'
  },
  '&:hover': {
    backgroundColor: '#D8E6FF'
  }
};

const getPillSelectInputSx = (menuOpen, openUp) => ({
  borderRadius: controlTokens.radius,
  borderTopLeftRadius: menuOpen && openUp ? 0 : controlTokens.radius,
  borderTopRightRadius: menuOpen && openUp ? 0 : controlTokens.radius,
  borderBottomLeftRadius: menuOpen && !openUp ? 0 : controlTokens.radius,
  borderBottomRightRadius: menuOpen && !openUp ? 0 : controlTokens.radius,
  height: controlTokens.selectHeight,
  backgroundColor: '#FFFFFF',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '16px',
    paddingRight: '56px',
    fontFamily: 'Noto Sans',
    fontSize: 14,
    color: colors.heading,
    backgroundColor: '#FFFFFF'
  },
  '& .MuiSelect-icon, & .MuiSelect-iconOpen': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTopRightRadius: menuOpen && openUp ? 0 : controlTokens.radius,
    borderBottomRightRadius: menuOpen && !openUp ? 0 : controlTokens.radius,
    pointerEvents: 'none',
    transform: 'none'
  },
  '& .drawer-select-icon-divider': {
    borderLeft: `1px solid ${menuOpen ? controlTokens.focus : controlTokens.border}`
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: menuOpen ? controlTokens.focus : controlTokens.border,
    borderWidth: 1,
    borderTopColor: menuOpen && openUp ? 'transparent' : (menuOpen ? controlTokens.focus : controlTokens.border),
    borderTopWidth: menuOpen && openUp ? 0 : 1,
    borderBottomColor: menuOpen && !openUp ? 'transparent' : (menuOpen ? controlTokens.focus : controlTokens.border),
    borderBottomWidth: menuOpen && !openUp ? 0 : 1,
    borderTopLeftRadius: menuOpen && openUp ? 0 : controlTokens.radius,
    borderTopRightRadius: menuOpen && openUp ? 0 : controlTokens.radius,
    borderBottomLeftRadius: menuOpen && !openUp ? 0 : controlTokens.radius,
    borderBottomRightRadius: menuOpen && !openUp ? 0 : controlTokens.radius
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: controlTokens.focus,
    borderTopColor: menuOpen && openUp ? 'transparent' : controlTokens.focus,
    borderBottomColor: menuOpen && !openUp ? 'transparent' : controlTokens.focus,
    borderWidth: 2,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: controlTokens.focus,
    borderWidth: 2,
    borderTopColor: menuOpen && openUp ? 'transparent' : controlTokens.focus,
    borderTopWidth: menuOpen && openUp ? 0 : 2,
    borderBottomColor: menuOpen && !openUp ? 'transparent' : controlTokens.focus,
    borderBottomWidth: menuOpen && !openUp ? 0 : 2,
    borderTopLeftRadius: menuOpen && openUp ? 0 : controlTokens.radius,
    borderTopRightRadius: menuOpen && openUp ? 0 : controlTokens.radius,
    borderBottomLeftRadius: menuOpen && !openUp ? 0 : controlTokens.radius,
    borderBottomRightRadius: menuOpen && !openUp ? 0 : controlTokens.radius
  },
  '&:hover .drawer-select-icon-divider': {
    borderLeftColor: controlTokens.focus
  },
  '&.Mui-focused .drawer-select-icon-divider': {
    borderLeftColor: controlTokens.focus
  }
});

const PillSelectIcon = (props) => {
  const { className, ...rest } = props || {};
  const isOpen = className?.includes('MuiSelect-iconOpen');
  const composedClass = className ? `${className} drawer-select-icon` : 'drawer-select-icon';

  return (
    <Box
      component="span"
      className={composedClass}
      sx={{
        width: 48,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        pointerEvents: 'none'
      }}
    >
      <Box
        component="span"
        className="drawer-select-icon-divider"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          borderLeft: `1px solid ${controlTokens.border}`
        }}
      />
      <IconChevronDown
        stroke={2}
        size={16}
        color={colors.heading}
        style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 120ms ease'
        }}
        {...rest}
      />
    </Box>
  );
};

/**
 * A styled select component with pill-shaped design
 * @param {any} value - The selected value
 * @param {function} onChange - Callback function when selection changes
 * @param {Array} options - Array of options with {value, label, isDivider} structure
 * @param {string|number} width - Width of the select (default: '100%')
 * @param {object} sx - Additional sx props for the FormControl
 */
const PillSelect = ({ value, onChange, options, width = '100%', sx = {}, menuMaxHeight }) => {
  const controlRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);

  const handleOpen = () => {
    const rect = controlRef.current?.getBoundingClientRect?.();
    if (rect) {
      const estimatedMenuHeight = typeof menuMaxHeight === 'number' ? menuMaxHeight : 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
    } else {
      setOpenUp(false);
    }

    setMenuOpen(true);
  };

  const handleClose = () => {
    setMenuOpen(false);
  };

  const dropdownShadow = openUp ? '2px -2px 6px rgba(0, 0, 0, 0.14)' : '2px 2px 6px rgba(0, 0, 0, 0.14)';

  const menuProps = {
    ...selectMenuProps,
    anchorOrigin: { vertical: openUp ? 'top' : 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: openUp ? 'bottom' : 'top', horizontal: 'left' },
    PaperProps: {
      ...selectMenuProps.PaperProps,
      sx: {
        ...selectMenuPaperSx,
        borderRadius: openUp ? '16px 16px 0 0' : '0 0 16px 16px',
        boxShadow: dropdownShadow,
        ...(menuMaxHeight
          ? {
              maxHeight: menuMaxHeight,
              overflowY: 'auto',
              overflowX: 'hidden'
            }
          : null)
      }
    }
  };

  return (
    <FormControl ref={controlRef} sx={{ width, ...sx }}>
      <Select
        value={value}
        onChange={onChange}
        open={menuOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={PillSelectIcon}
        input={<OutlinedInput sx={getPillSelectInputSx(menuOpen, openUp)} />}
        MenuProps={menuProps}
      >
        {options.map((option, index) => {
          if (option.isDivider) {
            return (
              <Box key={`divider-${index}`}>
                {option.label && (
                  <Typography
                    sx={{
                      fontFamily: 'Rubik',
                      leadingTrim: 'both',
                      fontStyle: 'normal',
                      textEdge: 'cap',
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: '21px',
                      letterSpacing: '0.14px',
                      color: colors.heading,
                      backgroundColor: '#FFFFFF',
                      padding: '16px 16px 8px 16px',
                      mt: 0.5,
                      borderTop: `1px solid ${colors.border}`
                    }}
                  >
                    {option.label}
                  </Typography>
                )}
              </Box>
            );
          }
          
          // Check if there's any divider before this item
          const hasDividerBefore = options.slice(0, index).some(opt => opt.isDivider);
          
          return (
            <MenuItem 
              key={option.value} 
              value={option.value} 
              sx={{
                ...menuItemBaseSx,
                ...(hasDividerBefore && { pl: '26px' })
              }}
            >
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default PillSelect;
